import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import DenmarkMap from './DenmarkMap';
import { ErrorBoundary } from 'react-error-boundary';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Helmet } from 'react-helmet'; // Add this import
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';  // Add this import
import { Tab } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';  // Add this import

// Add error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="p-4 bg-red-500/10 rounded-lg">
            <h2 className="text-xl font-bold mb-2">Something went wrong:</h2>
            <pre className="text-sm text-red-200">{error.message}</pre>
            <button 
                onClick={resetErrorBoundary}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
                Try again
            </button>
        </div>
    );
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    annotationPlugin  // Register the plugin this way
);

// Add this helper function at the top
const validatePriceData = (data) => {
    if (!data || !data.hourly || !Array.isArray(data.hourly)) {
        return false;
    }
    return true;
};

// Add these helper functions after validatePriceData
const getCurrentPrice = (hourlyPrices) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const currentHour = new Date().getHours();
    return hourlyPrices[currentHour];
};

const getLowestPrice = (hourlyPrices) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const lowest = Math.min(...hourlyPrices);
    const hour = hourlyPrices.indexOf(lowest);
    return { price: lowest, hour };
};

const getHighestPrice = (hourlyPrices) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const highest = Math.max(...hourlyPrices);
    const hour = hourlyPrices.indexOf(highest);
    return { price: highest, hour };
};

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

const pricingStyles = {
    container: {
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: '15px',
        padding: '20px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        color: '#333',
        fontFamily: 'Arial, sans-serif',
        transition: 'background 0.5s ease',
    },
    header: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333',
    },
    gradientBackground: {
        background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
        animation: 'gradient 3s ease infinite',
    },
    '@keyframes gradient': {
        '0%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
        '100%': { backgroundPosition: '0% 50%' },
    },
    interactiveElement: {
        cursor: 'pointer',
        transition: 'transform 0.2s',
    },
    interactiveElementHover: {
        transform: 'scale(1.05)',
    },
};

const ElectricityPricingContent = ({
    defaultRegion = 'DK1',
    regions = ['DK1', 'DK2'],
    regionColors = {
        'DK1': '#59a14f',
        'DK2': '#4e79a7',
    },
    getRegionColorClass = (region) => {
        const colors = {
            'DK1': 'bg-green-500',
            'DK2': 'bg-blue-500',
        };
        return colors[region] || 'bg-gray-500';
    },
    MapComponent = DenmarkMap,
    apiEndpoint = '/api/danish-prices',
    countryName = 'Denmark'
}) => {
    const [data, setData] = useState({ today: '', Danishprices: {} });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(defaultRegion);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [predictedPrices, setPredictedPrices] = useState({ tomorrow: {} });  // Remove dayAfterTomorrow
    const [selectedDate, setSelectedDate] = useState('today');

    useEffect(() => {
        const fetchPricesWithRetry = async (retryCount = 0) => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}${apiEndpoint}`);
                
                // Normalize the data structure
                const normalizedData = {
                    today: response.data.today || '',
                    Danishprices: response.data.Danishprices || {}
                };

                setData(normalizedData);
                const predictionResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/danish-prices/predictions`);
                setPredictedPrices(predictionResponse.data);
                setError(null);
                window.dispatchEvent(new Event('electricityPricingLoaded'));
            } catch (err) {
                console.error(`Attempt ${retryCount + 1} failed:`, err);
                if (retryCount < MAX_RETRIES) {
                    setTimeout(() => fetchPricesWithRetry(retryCount + 1), RETRY_DELAY);
                } else {
                    setError('Could not load electricity prices. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPricesWithRetry();
    }, [apiEndpoint]);

    if (error) return <div>{error}</div>;

    const generateChartData = (prices) => {
        const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);
        const currentHour = new Date().getHours();
        
        const datasets = Object.entries(prices).flatMap(([region, regionData]) => {
            if (!regionData || !Array.isArray(regionData.hourly)) {
                console.error(`Skipping region ${region} due to invalid data`, regionData);
                return [];
            }

            const paddedData = [...regionData.hourly];
            while (paddedData.length < 24) paddedData.push(null);

            return {
                label: region,
                data: paddedData.map(price => price !== null ? price.toFixed(2) : null),  // Already in DKK/kWh
                borderColor: regionColors[region],
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    
                    if (!chartArea) return regionColors[region] + '20'; // Fallback

                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, `${regionColors[region]}40`); // 25% opacity at top
                    gradient.addColorStop(1, `${regionColors[region]}00`); // 0% opacity at bottom
                    return gradient;
                },
                fill: 'start',
                stepped: 'before',
                tension: 0.4,
            };
        });

        return { labels: hours, datasets };
    };

    const getCurrentDataset = () => {
        switch (selectedDate) {
            case 'tomorrow':
                return data.predictedPrices?.tomorrow || {};
            default:
                return data.Danishprices;
        }
    };

    const getFormattedDate = () => {
        const date = new Date();
        switch (selectedDate) {
            case 'tomorrow':
                date.setDate(date.getDate() + 1);
                break;
            default:
                break;
        }
        return date.toLocaleDateString('sv-SE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 100,
        events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'], // Limit event listeners
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            annotation: {
                annotations: {
                    currentTime: {
                        display: selectedDate === 'today', // Only show for today
                        type: 'line',
                        xMin: new Date().getHours(),
                        xMax: new Date().getHours(),
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        borderWidth: 2,
                        borderDash: [6, 6],
                        label: {
                            display: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            content: (ctx) => {
                                const currentPrice = getCurrentPrice(data.Danishprices[selectedRegion]?.hourly);
                                return [`Nu`, `${currentPrice?.toFixed(2) ?? '-'} kr/kWh`];
                            },
                            position: 'start',
                            padding: 6,
                            font: {
                                size: 12
                            }
                        }
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: {
                    size: 14,
                    family: 'system-ui'
                },
                bodyFont: {
                    size: 13,
                    family: 'system-ui'
                },
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                callbacks: {
                    label: function(tooltipItem) {
                        const timeRange = `${tooltipItem.label}–${(parseInt(tooltipItem.label) + 1) % 24}:00`;
                        const currentHour = new Date().getHours();
                        const isCurrentHour = parseInt(tooltipItem.label) === currentHour;
                        
                        let label = `${tooltipItem.dataset.label}: ${tooltipItem.raw?.toFixed(2) ?? '-'} kr/kWh`;
                        if (isCurrentHour) {
                            label += ' (Aktuellt pris)';
                        }
                        return `${label} (${timeRange})`;
                    },
                    title: function(tooltipItems) {
                        const item = tooltipItems[0];
                        const hour = parseInt(item.label);
                        const currentHour = new Date().getHours();
                        if (hour === currentHour) {
                            return `${item.label}:00 (Nu)`;
                        }
                        return `${item.label}:00`;
                    }
                }
            },
            legend: {
                position: 'top',
                align: 'start',
                labels: {
                    color: '#ffffff',
                    padding: 20,
                    font: {
                        family: 'system-ui',
                        size: 13
                    },
                    usePointStyle: true,
                    pointStyle: 'rect'
                }
            }
        },
        scales: {
            y: {
                border: {
                    display: false
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.05)',
                    drawTicks: false
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.7)',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value) {
                        return value.toFixed(2) + ' kr';
                    }
                }
            },
            x: {
                border: {
                    display: false
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.7)',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value) {
                        return value + ':00';
                    }
                }
            }
        }
    };

    const MemoizedChart = React.memo(({ data, options }) => (
        <Line data={data} options={options} redraw={false} />
    ));

    const formattedStr = data.today ? `${data.today.slice(0, 4)}-${data.today.slice(4, 6)}-${data.today.slice(6, 8)}` : '';
    const formattedDate = formattedStr ? new Date(formattedStr).toLocaleDateString('sv-SE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }) : 'Invalid date';

    const handleRegionSelect = (region) => {
        setSelectedRegion(region);
    };

    // Add error boundary for chart rendering
    const renderChart = () => {
        try {
            const chartData = generateChartData(
                selectedRegion === 'ALL' 
                    ? getCurrentDataset() 
                    : { [selectedRegion]: getCurrentDataset()[selectedRegion] }
            );

            if (!chartData || !chartData.datasets || !chartData.labels) {
                console.error('Invalid chart data:', chartData);
                return <div>Error: Invalid data format</div>;
            }

            return (
                <MemoizedChart 
                    data={chartData} 
                    options={chartOptions}
                />
            );
        } catch (error) {
            console.error('Chart rendering error:', error);
            return <div>Error rendering chart</div>;
        }
    };

    return (
        <div className="min-h-screen overflow-y-auto relative bg-gradient-to-r from-purple1 via-purple2 to-purple4 text-white py-8 md:py-12">
            <div className="container mx-auto px-4 md:px-8">
                <h1 className="text-3xl font-bold mb-8">Elpriser i {countryName}</h1>
                {/* Hero Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
                    <div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                            {/* Existing price cards code */}
                            {!loading && Object.entries(data.Danishprices).map(([area, priceData]) => {
                                if (!priceData?.hourly) {
                                    console.error(`Skipping area card for ${area} (no data)`);
                                    return null;
                                }
                                return (
                                    <div 
                                        key={area} 
                                        className={`bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-2 sm:p-4 cursor-pointer 
                                            transform transition-all hover:-translate-y-1 hover:scale-102 hover:shadow-xl
                                            ${selectedRegion === area ? 'ring-2 ring-white/30' : ''}`}
                                        onClick={() => handleRegionSelect(area)}
                                    >
                                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(area)}`}></div>
                                        <div className="text-center">
                                            <h3 className="text-base sm:text-lg font-bold">{area}</h3>
                                            <p className="text-xl sm:text-2xl font-extrabold mt-0.5 sm:mt-1">
                                                {(parseFloat(priceData.average) * 100).toFixed(1)}
                                                <span className="text-sm sm:text-base opacity-70 ml-1">øre</span>
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            {loading && Array(4).fill(0).map((_, index) => (
                                <div key={index} className="animate-pulse bg-white/5 rounded-lg h-[80px] sm:h-[120px]" />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">  {/* Change from 2 to 3 columns */}
                    <div className="lg:col-span-2 space-y-6">  {/* Span 2 columns for the chart section */}
                        {/* Chart Section */}
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                                <h2 className="text-2xl font-bold mb-4 md:mb-0">{getFormattedDate()}</h2>
                                <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-4">
                                    {/* Date selection buttons */}
                                    <div className="flex space-x-2">
                                        <button 
                                            onClick={() => setSelectedDate('today')}
                                            className={`px-4 py-2 rounded-lg ${
                                                selectedDate === 'today' ? 'bg-white/20' : 'bg-white/5'
                                            }`}
                                        >
                                            Idag
                                        </button>
                                        <button 
                                            onClick={() => setSelectedDate('tomorrow')}
                                            className={`px-4 py-2 rounded-lg ${
                                                selectedDate === 'tomorrow' ? 'bg-white/20' : 'bg-white/5'
                                            }`}
                                        >
                                            Imorgon
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="h-[450px]">
                                {loading ? (
                                    <div className="animate-pulse h-full bg-white/5" />
                                ) : (
                                    renderChart()
                                )}
                            </div>
                        </div>

                        {/* Current, Lowest, and Highest Price Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                            <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                                <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-sm md:text-base font-bold mb-1">Aktuellt Pris</h3>
                                        <p className="text-xs md:text-sm text-white/50">Just nu</p>
                                    </div>
                                    <p className="text-lg md:text-xl font-bold">
                                        {getCurrentPrice(data.Danishprices[selectedRegion]?.hourly)?.toFixed(2) ?? '-'}
                                        <span className="text-xs md:text-sm opacity-70 ml-1">kr/kWh</span>
                                    </p>
                                </div>
                            </div>

                            <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                                <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-sm md:text-base font-bold mb-1">Lägsta Pris</h3>
                                        <p className="text-xs md:text-sm text-white/50">
                                            Kl. {formatHour(getLowestPrice(data.Danishprices[selectedRegion]?.hourly)?.hour)}
                                        </p>
                                    </div>
                                    <p className="text-lg md:text-xl font-bold">
                                        {getLowestPrice(data.Danishprices[selectedRegion]?.hourly)?.price.toFixed(2) ?? '-'}
                                        <span className="text-xs md:text-sm opacity-70 ml-1">kr/kWh</span>
                                    </p>
                                </div>
                            </div>

                            <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                                <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-sm md:text-base font-bold mb-1">Högsta Pris</h3>
                                        <p className="text-xs md:text-sm text-white/50">
                                            Kl. {formatHour(getHighestPrice(data.Danishprices[selectedRegion]?.hourly)?.hour)}
                                        </p>
                                    </div>
                                    <p className="text-lg md:text-xl font-bold">
                                        {getHighestPrice(data.Danishprices[selectedRegion]?.hourly)?.price.toFixed(2) ?? '-'}
                                        <span className="text-xs md:text-sm opacity-70 ml-1">kr/kWh</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Interactive Map Section */}
                    <div className="lg:col-span-1 hidden lg:block sticky top-6 space-y-4">  {/* Span 1 column for the map section */}
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
                            <MapComponent 
                                onRegionSelect={handleRegionSelect} 
                                selectedRegion={selectedRegion}
                            />
                            <RegionInfo 
                                region={selectedRegion} 
                                data={data.Danishprices[selectedRegion]} 
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Wrap main component with ErrorBoundary
const ElectricityPricing = (props) => {
    return (
        <ErrorBoundary 
            FallbackComponent={ErrorFallback}
            onReset={() => window.location.reload()}
        >
            <Helmet>
                <title>Danska elpriser</title>
            </Helmet>
            <ElectricityPricingContent {...props} />
        </ErrorBoundary>
    );
};

// Export the component wrapped in React.memo
export default React.memo(ElectricityPricing);

// Add this helper function for region colors
const getRegionColorClass = (region) => {
    const colors = {
        'DK1': 'bg-green-500',
        'DK2': 'bg-blue-500',
    };
    return colors[region] || 'bg-gray-500';
};

export const PriceCards = ({ data, loading, selectedRegion, getRegionColorClass, getCurrentPrice, getLowestPrice, getHighestPrice, formatHour }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {loading ? (
                Array(3).fill(0).map((_, index) => (
                    <div key={index} className="animate-pulse bg-white/5 rounded-lg h-[80px] md:h-[100px]" />
                ))
            ) : (
                <>
                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-sm md:text-base font-bold mb-1">Aktuellt Pris</h3>
                                <p className="text-xs md:text-sm text-white/50">Just nu</p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {getCurrentPrice(data.Danishprices[selectedRegion]?.hourly)?.toFixed(2) ?? '-'}
                                <span className="text-xs md:text-sm opacity-70 ml-1">kr/kWh</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-sm md:text-base font-bold mb-1">Lägsta Pris</h3>
                                <p className="text-xs md:text-sm text-white/50">
                                    Kl. {formatHour(getLowestPrice(data.Danishprices[selectedRegion]?.hourly)?.hour)}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {getLowestPrice(data.Danishprices[selectedRegion]?.hourly)?.price.toFixed(2) ?? '-'}
                                <span className="text-xs md:text-sm opacity-70 ml-1">kr/kWh</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-sm md:text-base font-bold mb-1">Högsta Pris</h3>
                                <p className="text-xs md:text-sm text-white/50">
                                    Kl. {formatHour(getHighestPrice(data.Danishprices[selectedRegion]?.hourly)?.hour)}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {getHighestPrice(data.Danishprices[selectedRegion]?.hourly)?.price.toFixed(2) ?? '-'}
                                <span className="text-xs md:text-sm opacity-70 ml-1">kr/kWh</span>
                            </p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const RegionInfo = ({ region, data }) => {
    return (
        <div className="mt-4 space-y-4">
            <h3 className="text-xl font-bold">Region {region}</h3>
            <div className="text-sm space-y-1">
                <p>
                    <InformationCircleIcon className="inline h-4 w-4 mr-1" />
                    Genomsnittspris: {data?.average ? `${data.average.toFixed(2)} kr/kWh` : '-'}
                </p>
            </div>
        </div>
    );
};
